/**
 * TagChips — display + inline edit for a list of kebab-case tags.
 *
 * Props:
 *   - tags: string[]
 *   - editable?: boolean                 (show edit controls)
 *   - onChange?: (newTags: string[]) => Promise | void   (called on save)
 *   - onTagClick?: (tag: string) => void (optional; e.g. for filtering)
 *   - compact?: boolean                  (smaller chips, no border around wrapper)
 *   - testidPrefix?: string
 *
 * Normalisation happens on the backend; we still lowercase + trim + dedupe
 * client-side so the preview matches what gets stored.
 */
import React, { useState } from 'react';

const MAX_TAGS = 8;
const MAX_LEN = 24;

function normalize(raw) {
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const t of raw) {
    if (typeof t !== 'string') continue;
    let s = t.toLowerCase().trim().replace(/[^a-z0-9\- ]+/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '');
    if (!s || s.length > MAX_LEN) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= MAX_TAGS) break;
  }
  return out;
}

export default function TagChips({
  tags = [], editable = false, onChange, onTagClick,
  compact = false, testidPrefix = 'tag',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tags);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => { setDraft(tags); setInput(''); setError(null); setEditing(true); };
  const cancel = () => { setEditing(false); setInput(''); setError(null); };

  const addFromInput = () => {
    const next = normalize([...draft, input]);
    if (next.length === draft.length && input.trim()) {
      setError(draft.length >= MAX_TAGS ? `Maximum ${MAX_TAGS} tags` : 'Tag rejected (check length / characters)');
    } else {
      setError(null);
    }
    setDraft(next);
    setInput('');
  };

  const removeDraft = (tag) => setDraft(draft.filter((t) => t !== tag));

  const save = async () => {
    if (!onChange) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      await onChange(normalize(draft));
      setEditing(false);
      setInput('');
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const chipSize = compact
    ? { fontSize: 10, padding: '2px 8px' }
    : { fontSize: 11, padding: '3px 10px' };

  const renderChip = (tag, { onRemove, clickable } = {}) => (
    <span
      key={tag}
      data-testid={`${testidPrefix}-chip-${tag}`}
      onClick={clickable && onTagClick ? (e) => { e.stopPropagation(); onTagClick(tag); } : undefined}
      style={{
        ...chipSize,
        borderRadius: 12,
        background: '#eef2ff',
        color: '#3730a3',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: clickable && onTagClick ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      #{tag}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(tag); }}
          data-testid={`${testidPrefix}-remove-${tag}`}
          style={{
            all: 'unset', cursor: 'pointer', fontSize: 12, lineHeight: 1,
            color: '#6366f1', marginLeft: 2,
          }}
          aria-label={`Remove ${tag}`}
        >
          ×
        </button>
      )}
    </span>
  );

  if (!editing) {
    const empty = !tags || tags.length === 0;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
           data-testid={`${testidPrefix}-chips`}>
        {empty && !editable && (
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>No tags</span>
        )}
        {tags.map((t) => renderChip(t, { clickable: true }))}
        {editable && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={startEdit}
            data-testid={`${testidPrefix}-edit-btn`}
            style={{ fontSize: 10, padding: '2px 8px' }}
          >
            {empty ? '+ Add tags' : 'Edit tags'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
         data-testid={`${testidPrefix}-editor`}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {draft.map((t) => renderChip(t, { onRemove: removeDraft }))}
        {draft.length < MAX_TAGS && (
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addFromInput(); }
              if (e.key === 'Backspace' && !input && draft.length) {
                setDraft(draft.slice(0, -1));
              }
            }}
            placeholder="Add tag + Enter"
            data-testid={`${testidPrefix}-input`}
            style={{
              padding: '3px 6px', fontSize: 11, border: '1px solid var(--border)',
              borderRadius: 4, minWidth: 100,
            }}
          />
        )}
      </div>
      {error && (
        <div style={{ fontSize: 10, color: 'var(--danger)' }}
             data-testid={`${testidPrefix}-error`}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="btn btn-primary btn-sm"
                onClick={save} disabled={busy}
                data-testid={`${testidPrefix}-save-btn`}
                style={{ fontSize: 10, padding: '3px 10px' }}>
          {busy ? 'Saving…' : 'Save tags'}
        </button>
        <button type="button" className="btn btn-outline btn-sm"
                onClick={cancel} disabled={busy}
                data-testid={`${testidPrefix}-cancel-btn`}
                style={{ fontSize: 10, padding: '3px 10px' }}>
          Cancel
        </button>
        <span style={{ fontSize: 10, color: 'var(--text-secondary)', alignSelf: 'center' }}>
          {draft.length}/{MAX_TAGS} · lowercase · max {MAX_LEN} chars
        </span>
      </div>
    </div>
  );
}
